import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Drawer, List, ListItemIcon, ListItemText, Box, Collapse } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EmailIcon from "@mui/icons-material/Email";
import SettingsIcon from "@mui/icons-material/Settings";
import SummarizeIcon from "@mui/icons-material/Assessment";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import MenuIcon from "@mui/icons-material/Menu";

import ListItemButton from "@mui/material/ListItemButton";

const useStyles = makeStyles(() => ({
  drawer: {
    width: (props) => props.drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: (props) => props.drawerWidth,
    backgroundColor: "#FFFFFF",
    color: "#3A4D54",
    borderRight: "none",
    boxShadow: "1px 0px 0px 0px rgba(0,0,0,0.15)",
    top: 68,
  },
  hamburger: {
    padding: "15px 28px",
  },
  hamburgerIcon: {
    color: "#3A4D54",
    fontSize: 24,
  },
  divider: {
    height: 1,
    backgroundColor: "rgba(0,0,0,0.15)",
    margin: 0,
  },
  list: {
    paddingTop: 8,
  },
  listItem: {
    height: 46,
    paddingLeft: 22,
    position: "relative",
    "&.Mui-selected": {
      backgroundColor: "#EBEDEE",
      "&:hover": {
        backgroundColor: "#EBEDEE",
      },
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 8,
        width: 5,
        height: 30,
        backgroundColor: "#3A4D54",
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
      },
    },
    "&:hover": {
      backgroundColor: "#F9FAFB",
    },
  },
  icon: {
    color: "#3A4D54",
    minWidth: 41,
  },
  itemText: {
    "& .MuiListItemText-primary": {
      fontWeight: 700,
      fontSize: 13,
      color: "#3A4D54",
    },
  },
  childItem: {
    height: 42,
    paddingLeft: 63,
    position: "relative",
    "&.Mui-selected": {
      backgroundColor: "#EBEDEE",
      "&:hover": { backgroundColor: "#EBEDEE" },
      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 6,
        width: 5,
        height: 30,
        backgroundColor: "#3A4D54",
        borderTopRightRadius: 4,
        borderBottomRightRadius: 4,
      },
    },
    "&:hover": { backgroundColor: "#F9FAFB" },
  },
  childText: {
    "& .MuiListItemText-primary": {
      fontWeight: 500,
      fontSize: 13,
      color: "#3A4D54",
    },
  },
  expandIcon: {
    color: "#98A2B3",
    fontSize: 20,
  },
}));

// hidden: not part of the partner-management launch — remove the flag (and
// restore the matching route in AdminLayout.js) to bring a module back.
const menuItems = [
  { label: "Home", path: "/admin/dashboard", icon: <HomeIcon />, hidden: true },
  {
    label: "Partner Management",
    icon: <PeopleIcon />,
    children: [
      { label: "Partners", path: "/admin/partners" },
      { label: "Access Rights", path: "/admin/partner-access-rights" },
    ],
  },
  { label: "Companies", path: "/admin/companies", icon: <BusinessIcon /> },
  { label: "Users", path: "/admin/users", icon: <PersonIcon />, hidden: true },
  { label: "Approvals", path: "/admin/approvals", icon: <CheckCircleOutlineIcon />, hidden: true },
  { label: "Consumer Engagement", path: "/admin/consumer-engagement", icon: <PeopleIcon />, hidden: true },
  { label: "Email Log", path: "/admin/email-log", icon: <EmailIcon />, hidden: true },
  { label: "Master Control", path: "/admin/master-control", icon: <SettingsIcon />, hidden: true },
  { label: "Data Summary", path: "/admin/data-summary", icon: <SummarizeIcon />, hidden: true },
  { label: "Serial Number Allocation", path: "/admin/management-sn", icon: <QrCode2Icon />, hidden: true },
  { label: "Admins", path: "/admin/admins", icon: <AdminPanelSettingsIcon />, hidden: true },
];

export default function Sidebar({ drawerWidth }) {
  const classes = useStyles({ drawerWidth });
  const history = useHistory();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});

  // Parents with children default to expanded.
  const isMenuOpen = (item) => (openMenus[item.label] !== undefined ? openMenus[item.label] : true);

  return (
    <Drawer className={classes.drawer} variant="permanent" classes={{ paper: classes.drawerPaper }}>
      <Box className={classes.hamburger}>
        <MenuIcon className={classes.hamburgerIcon} />
      </Box>
      <Box className={classes.divider} />
      <List className={classes.list}>
        {menuItems.filter((item) => !item.hidden).map((item) => (item.children ? (
          <React.Fragment key={item.label}>
            <ListItemButton
              className={classes.listItem}
              selected={!isMenuOpen(item) && item.children.some((child) => location.pathname.startsWith(child.path))}
              onClick={() => setOpenMenus((prev) => ({ ...prev, [item.label]: !isMenuOpen(item) }))}>
              <ListItemIcon className={classes.icon}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} className={classes.itemText} />
              {isMenuOpen(item)
                ? <ExpandLess className={classes.expandIcon} />
                : <ExpandMore className={classes.expandIcon} />}
            </ListItemButton>
            <Collapse in={isMenuOpen(item)} timeout="auto" unmountOnExit>
              <List disablePadding>
                {item.children.map((child) => (
                  <ListItemButton
                    key={child.path}
                    className={classes.childItem}
                    selected={location.pathname.startsWith(child.path)}
                    onClick={() => history.push(child.path)}>
                    <ListItemText primary={child.label} className={classes.childText} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ) : (
          <ListItemButton
            key={item.path}
            className={classes.listItem}
            selected={location.pathname.startsWith(item.path)}
            onClick={() => history.push(item.path)}>
            <ListItemIcon className={classes.icon}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} className={classes.itemText} />
          </ListItemButton>
        )))}
      </List>
    </Drawer>
  );
}
